import { MatrixNotesCounter } from '@/components/matrix-grid/matrix-notes-counter';
import { MatrixStateBadge } from '@/components/matrix-grid/matrix-state-badge';
import { PivotPill } from '@/components/matrix-grid/pivot-pill';
import { matrixDragProps } from '@/lib/matrix-drag';
import { formatMatrixDate, matrixLinkTarget, matrixTitle } from '@/lib/matrix-grid-filters';
import { matrixDisplayState } from '@/lib/matrix-grid-search';
import { cn } from '@/lib/utils';
import {
  type MatrixSummary,
  resolveDestinataireLabel,
  resolveDestinataireValue,
} from '@mss_creator/shared';
import { Card, Icon, type RenderLink } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import type { ComponentProps, ReactNode } from 'react';

export interface MatrixCardProps {
  matrix: MatrixSummary;
  /** The « ⋯ » menu, rendered top right above the card's link. */
  actions?: ReactNode;
  /** Briefly true for a copy that just appeared (« Dupliquer »). */
  highlighted?: boolean;
  /**
   * The name of the folder the matrix sits in, shown under the sujet. Given in
   * « Toutes les matrices » only; omitted elsewhere, and for a matrix hors dossier.
   */
  folderName?: string | null | undefined;
}

/** The package hands over a concrete `href`; TanStack's `Link` takes it as is. */
const renderLink: RenderLink = ({ href, children, ...rest }) => (
  <Link {...({ to: href } as ComponentProps<typeof Link>)} {...rest}>
    {children}
  </Link>
);

/**
 * Grid card for one matrix (issue #16, planche 03): state badge, sujet on two
 * lines at most (so « (copie) » stays visible), pivot pill, point of view,
 * dimension count, « Étape N sur 4 » for a draft (Échec included), and the
 * « Créée le … par … » footer. Since #19 it also carries the counter of open
 * notes beside the « ⋯ » menu (with the memo and last note on hover), the name
 * of its folder, and it can be dragged onto a folder of the column.
 *
 * The package's `Card` with a stretched title link (`titleHref`): the whole
 * card leads to the matrix page once généré, to the wizard otherwise (which
 * lands on the right step via `deriveInitialStep`), while the « ⋯ » button in
 * `trailing` stays outside the anchor. The drag handle, the highlight ring and
 * the data attributes sit on a wrapper, since the card takes none of them.
 */
export function MatrixCard({
  matrix,
  actions,
  highlighted = false,
  folderName = null,
}: MatrixCardProps) {
  const state = matrixDisplayState(matrix);
  const isDraft = state === 'brouillon' || state === 'echec';
  const destinataireLabel = resolveDestinataireLabel(matrix);
  const hasDestinataire = resolveDestinataireValue(matrix) !== null;
  const href = matrixLinkTarget(matrix).replace('$matrixId', matrix.id);

  return (
    <div
      data-matrix-id={matrix.id}
      data-highlighted={highlighted || undefined}
      {...matrixDragProps(matrix.id)}
      className={cn(
        'h-full rounded-lg *:h-full',
        highlighted && 'ring-2 ring-bleu-ciel ring-offset-2',
      )}
    >
      <Card
        title={matrixTitle(matrix)}
        titleHref={href}
        renderLink={renderLink}
        visual={<MatrixStateBadge state={state} />}
        trailing={
          <div className="flex items-center gap-0">
            <MatrixNotesCounter matrix={matrix} />
            {actions}
          </div>
        }
        footerMeta={
          <>
            Créée le {formatMatrixDate(matrix.createdAt)}
            {matrix.createdBy && (
              <>
                {' par '}
                <span className="font-semibold">{matrix.createdBy.displayName}</span>
              </>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-2">
          {folderName !== null && (
            <p className="flex items-center gap-1 text-caption text-gray-600">
              <Icon name="folder" size={14} />
              <span className="truncate">{folderName}</span>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <PivotPill pivot={matrix.pivot} />
            <span
              className={cn(
                'inline-flex min-w-0 items-center gap-1 text-caption',
                hasDestinataire ? 'text-gray-700' : 'text-gray-500',
              )}
            >
              <span className="text-gray-500">
                <Icon name="send" size={14} />
              </span>
              <span className="shrink-0">Destinataire :</span>
              {/* The free text of « Autre » is cut with an ellipsis (issues #21 and #25, planche 20). */}
              <span title={destinataireLabel} className="max-w-48 truncate">
                {destinataireLabel}
              </span>
            </span>
          </div>
          <p className="flex items-center gap-1 text-caption text-gray-500">
            <span>
              {matrix.dimensionCount} {matrix.dimensionCount > 1 ? 'dimensions' : 'dimension'}
            </span>
            {isDraft && (
              <>
                <span aria-hidden="true" className="text-gray-300">
                  ·
                </span>
                <span>Étape {matrix.currentStep} sur 4</span>
              </>
            )}
          </p>
        </div>
      </Card>
    </div>
  );
}
